import React, { useContext, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import { getInventoryByStore } from "../../../api/inventoryApi";
import { LanguageContext } from "../../../components/LanguageContext";

const NumericCell = ({ value, icon, tone }) => (
    <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
    >
        {icon &&
            React.cloneElement(icon, {
                sx: {
                    fontSize: "1rem",
                    color: tone === "muted" ? "text.secondary" : `${tone}.main`,
                },
            })}
        <Typography
            sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: tone === "muted" ? "text.primary" : `${tone}.main`,
            }}
        >
            {value}
        </Typography>
    </Stack>
);

const LagerByStorePage = () => {
    const { storeCode } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const { data, isLoading } = useQuery({
        queryKey: ["inventory", storeCode],
        queryFn: () => getInventoryByStore(storeCode),
        staleTime: 5 * 60 * 1000,
    });

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminInventoryPageId || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "store_code",
                headerName:
                    translations?.adminInventoryPageStoreCode || "Šifra prodavnice",
                minWidth: 150,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "code",
                headerName:
                    translations?.adminInventoryPageProductCode || "Šifra proizvoda",
                minWidth: 150,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={(theme) => ({
                            fontFamily: "monospace",
                            fontWeight: 600,
                            bgcolor:
                                theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(15,23,42,0.04)",
                        })}
                    />
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminInventoryPageProductName ||
                    "Naziv proizvoda",
                minWidth: 280,
                flex: 2,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "amount",
                headerName: translations?.adminInventoryPageAmount || "Količina",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <NumericCell
                        value={params.value}
                        icon={<WarehouseOutlinedIcon />}
                        tone={params.value > 0 ? "success" : "error"}
                    />
                ),
            },
            {
                field: "on_order",
                headerName: translations?.onOrder || "Na narudžbi",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <NumericCell
                        value={params.value}
                        icon={<ShoppingCartOutlinedIcon />}
                        tone="warning"
                    />
                ),
            },
            {
                field: "mp_cena",
                headerName: translations?.mpCena || "MP Cena",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <NumericCell
                        value={params.value}
                        icon={<EuroOutlinedIcon />}
                        tone="muted"
                    />
                ),
            },
            {
                field: "nc",
                headerName: translations?.nCena || "NC",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <NumericCell
                        value={params.value}
                        icon={<EuroOutlinedIcon />}
                        tone="muted"
                    />
                ),
            },
        ],
        [translations]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<WarehouseOutlinedIcon />}
                eyebrow={translations?.inventoryByStore || "Inventory"}
                title={translations?.inventoryByStore || "Lager po prodavnici"}
                description={`${translations?.inventorySubtitle || "Pregled lagera prodavnice"} ${storeCode}`}
            />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    rowHeight={60}
                    emptyIcon={<Inventory2OutlinedIcon />}
                    emptyTitle={translations?.noInventoryTitle || "Nema podataka o lageru"}
                    emptyDescription={
                        translations?.noInventoryDescription ||
                        "Sinhronizujte lager iz Navigatora."
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default LagerByStorePage;